import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';

const squeezeQuery = window.matchMedia('(min-width: 990px)');

function getScrollContainer() {
  if (squeezeQuery.matches) {
    return document.querySelector('.page-wrapper') ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initThumbnails();
  }

  #controller = new AbortController();
  #thumbnailObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#thumbnailObserver?.disconnect();
  }

  #initThumbnails() {
    if (!this.classList.contains('media-gallery--has-thumbnails')) return;

    const { signal } = this.#controller;
    const grid = this.querySelector('.media-gallery__grid');
    const thumbnails = this.querySelectorAll('.media-gallery__thumbnail');
    const items = grid?.querySelectorAll('[data-media-index]');

    if (!grid || !thumbnails.length || !items?.length) return;

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener(
        'click',
        () => {
          const item = grid.querySelector(`[data-media-index="${thumbnail.dataset.thumbnailIndex}"]`);
          if (!item) return;

          const container = getScrollContainer();
          const containerRect = container.getBoundingClientRect();
          const itemRect = item.getBoundingClientRect();
          const offset = itemRect.top - containerRect.top - containerRect.height / 2 + itemRect.height / 2;

          container.scrollTo({
            top: container.scrollTop + offset,
            behavior: 'instant',
          });
        },
        { signal }
      );
    });

    this.#thumbnailObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const index = entry.target.dataset.mediaIndex;
          thumbnails.forEach((thumbnail) => {
            thumbnail.classList.toggle('is-active', thumbnail.dataset.thumbnailIndex === index);
          });
        });
      },
      { threshold: 0.5 }
    );

    items.forEach((item) => this.#thumbnailObserver?.observe(item));
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        MediaGallery.#scrollIntoViewIfNeeded(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Scrolls the gallery into view if it isn't currently visible, so switching a variant
   * (e.g. a color swatch) while scrolled further down the page is actually noticeable.
   *
   * @param {Element} element - The (new) media gallery element.
   */
  static #scrollIntoViewIfNeeded(element) {
    const { top } = element.getBoundingClientRect();
    const topIsVisible = top >= 0 && top < window.innerHeight;
    if (topIsVisible) return;

    element.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
